<template>
  <app-modal :modal-id="modalId" :title="modalTitle" :preloader="preloader"
                @submit="submit" @close-modal="closeModal" 
                modal-size="large">
        <template slot="header">
            <h5 class="modal-title" id="ModalLabel">{{ modalTitle }}</h5>
            <button type="button" class="close outline-none" @click.prevent="closeModal">
                <span>
                    <app-icon :name="'x'"></app-icon>
                </span>
            </button>
        </template>
        <template slot="body">
            <app-overlay-loader v-if="preloader" />
            <form class="mb-0" :class="{ 'loading-opacity': preloader }" ref="form"
                :data-url='selectedUrl ? `attendances/${inputs.id}` : `attendances`'>
                
                <!-- Mode de saisie -->
                <div class="row mb-3">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label>{{ $t('entry_mode') }}</label>
                            <div class="d-flex">
                                <div class="custom-control custom-radio mr-3">
                                    <input type="radio" id="single" name="entryMode" class="custom-control-input" 
                                           v-model="inputs.entry_mode" value="single">
                                    <label class="custom-control-label" for="single">{{ $t('single_entry') }}</label>
                                </div>
                                <div class="custom-control custom-radio">
                                    <input type="radio" id="simulation" name="entryMode" class="custom-control-input" 
                                           v-model="inputs.entry_mode" value="simulation">
                                    <label class="custom-control-label" for="simulation">{{ $t('simulation_mode') }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <!-- Employee Select Input -->
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="employee">
                                {{ $t('employee') }}
                                <span style="color: crimson">*</span>
                            </label>
                            <app-input class="w-100" id="employee" v-model="inputs.employee_id" 
                                    :list="employeeList" list-value-field="name" :placeholder="$t('choose_one')" 
                                    type="search-select" :required="true" />
                        </div>
                    </div>

                    <!-- Location Select Input -->
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="location">
                                {{ $t('location') }}
                                <span style="color: crimson">*</span>
                            </label>
                            <app-input class="w-100" id="location" v-model="inputs.location_id" 
                                    :list="locationList" list-value-field="name" :placeholder="$t('choose_one')" 
                                    type="search-select" :required="true" />
                        </div>
                    </div>

                    <!-- Single Entry Mode Fields -->
                    <template v-if="inputs.entry_mode === 'single'">
                        <!-- Event Date -->
                        <div class="col-md-6">
                            <app-form-group
                                :label="$t('event_date')"
                                type="date"
                                v-model="inputs.event_date"
                                :placeholder="$t('event_date')"
                                :required="true"
                            />
                        </div>

                        <!-- Event Time -->
                        <div class="col-md-6">
                            <app-form-group
                                :label="$t('event_time')"
                                type="time"
                                v-model="inputs.event_time"
                                :placeholder="$t('event_time')"
                                :required="true"
                            />
                        </div>

                        <!-- Event Type -->
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="event_type">
                                    {{ $t('event_type') }}
                                    <span style="color: crimson">*</span>
                                </label>
                                <app-input class="w-100" id="event_type" v-model="inputs.event_type"
                                        :list="eventTypeList" :placeholder="$t('choose_one')" type="select"
                                        :required="true" />
                            </div>
                        </div>
                    </template>

                    <!-- Simulation Mode Fields -->
                    <template v-if="inputs.entry_mode === 'simulation'">
                        <!-- Date Range -->
                        <div class="col-md-6">
                            <app-form-group
                                :label="$t('start_date')"
                                type="date"
                                v-model="inputs.start_date"
                                :placeholder="$t('start_date')"
                                :required="true"
                            />
                        </div>

                        <div class="col-md-6">
                            <app-form-group
                                :label="$t('end_date')"
                                type="date"
                                v-model="inputs.end_date"
                                :placeholder="$t('end_date')"
                                :required="true"
                            />
                        </div>

                        <!-- Working Hours -->
                        <div class="col-md-6">
                            <app-form-group
                                :label="$t('check_in_time')"
                                type="time"
                                v-model="inputs.check_in_time"
                                :placeholder="$t('check_in_time')"
                                :required="true"
                            />
                        </div>

                        <div class="col-md-6">
                            <app-form-group
                                :label="$t('check_out_time')"
                                type="time"
                                v-model="inputs.check_out_time"
                                :placeholder="$t('check_out_time')"
                                :required="true"
                            />
                        </div>

                        <!-- Weekend handling -->
                        <div class="col-md-12">
                            <div class="form-group">
                                <div class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input" id="skip_weekends" 
                                           v-model="inputs.skip_weekends">
                                    <label class="custom-control-label" for="skip_weekends">
                                        {{ $t('skip_weekends') }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Time variance -->
                        <div class="col-md-12">
                            <div class="form-group">
                                <label for="time_variance">{{ $t('time_variance_minutes') }}</label>
                                <app-input id="time_variance" type="number" min="0" max="60" v-model="inputs.time_variance"
                                    :placeholder="$t('time_variance_minutes')" />
                                <small class="form-text text-muted">{{ $t('time_variance_help') }}</small>
                            </div>
                        </div>
                    </template>
                </div>
            </form>
        </template>
        <template slot="footer">
            <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal" @click="closeModal">
                {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-primary" @click="submit" :disabled="preloader">
                <span v-if="preloader" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                {{ inputs.entry_mode === 'simulation' ? $t('generate_attendances') : $t('save') }}
            </button>
        </template>
    </app-modal>
</template>

<script>
import axios from 'axios';
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import {errorMessageForArray} from '../../../../common/Helper/Support/FormHelper';
import {formatDateForServer, formatDateTimeForServer} from "../../../../common/Helper/Support/DateTimeHelper";
import { ModalMixin } from '../../../Mixins/ModalMixin';
import {mapState} from 'vuex';

export default {
    name: 'AttendanceAddEditModal',
    mixins: [FormMixin, ModalMixin],
    props: {
        tableId: {
            type: String,
        },
    },
    computed: {
        ...mapState({
            employeeList: state => state.selects.employeeList,
            locationList: state => state.selects.locationList,
        })
    },
    data() {
        return {
            errorMessageForArray,
            preloader: false,
            modalId: 'attendance-add-edit-modal',
            inputs: {
                employee_id: '',
                location_id: '',
                event_date: new Date(),
                event_time: '',
                event_type: 'in',
                latitude: '',
                longitude: '',
                is_within_geofence: false,
                entry_mode: 'single',
                // Simulation fields
                start_date: '',
                end_date: '',
                check_in_time: '08:00',
                check_out_time: '17:00',
                skip_weekends: true,
                time_variance: 5,
            },
            isModalActive: false,
            modalTitle: `${this.$t('new-attendance')}`,
            eventTypeList: [
                {
                    id: 'in',
                    value: this.$t('in')
                },
                {
                    id: 'out',
                    value: this.$t('out')
                },
            ],
        }
    },
    methods: {
        submit() {
            this.preloader = true;
            let formData = { ...this.inputs };

            if (this.inputs.entry_mode === 'single') {
                // Single entry mode
                formData.event_date = moment(formData.event_date).format('YYYY-MM-DD');
                formData.event_time = `${formData.event_date} ${formData.event_time}`;
                delete formData.start_date;
                delete formData.end_date;
                delete formData.check_in_time;
                delete formData.check_out_time;
                delete formData.skip_weekends;
                delete formData.time_variance;
                delete formData.entry_mode;

                this.saveAttendance(formData);
            } else {
                // Simulation mode
                formData.start_date = formatDateForServer(formData.start_date);
                formData.end_date = formatDateForServer(formData.end_date);
                
                this.generateSimulationData(formData);
            }
        },

        saveAttendance(formData) {
            const url = this.selectedUrl ? `attendances/${this.inputs.id}` : 'attendances';
            const method = this.selectedUrl ? 'patch' : 'post';

            axios({
                method: method,
                url: url,
                data: formData,
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(response => {
                this.preloader = false;
                this.$toastr.s(response.data.message);
                this.$hub.$emit('reload-' + this.tableId);
                this.closeModal();
            })
            .catch(error => {
                this.preloader = false;
                this.errors = error.response?.data?.errors || {};
                if (error.response?.data?.message) {
                    this.$toastr.e(error.response.data.message);
                } else {
                    this.$toastr.e('An error occurred while saving attendance');
                }
            });
        },

        generateSimulationData(formData) {
            axios.post('attendances/simulate', formData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(response => {
                this.preloader = false;
                this.$toastr.s(response.data.message || `${response.data.count} présences générées avec succès`);
                this.$hub.$emit('reload-' + this.tableId);
                this.closeModal();
            })
            .catch(error => {
                this.preloader = false;
                this.errors = error.response?.data?.errors || {};
                if (error.response?.data?.message) {
                    this.$toastr.e(error.response.data.message);
                } else {
                    this.$toastr.e('An error occurred while generating simulation data');
                }
            });
        },

        afterSuccess(response) {
            this.$toastr.s(response.data.message);
            this.$hub.$emit('reload-' + this.tableId);
        },

        afterSuccessFromGetEditData({ data }) {
            this.inputs = data;
            this.inputs.event_time = data.event_time ? new Date(data.event_time) : '';
            this.preloader = false;
        },

        resetForm() {
            this.inputs = {
                employee_id: '',
                location_id: '',
                event_time: '',
                event_type: 'in',
                latitude: '',
                longitude: '',
                is_within_geofence: false,
                entry_mode: 'single',
                start_date: '',
                end_date: '',
                check_in_time: '08:00',
                check_out_time: '17:00',
                skip_weekends: true,
                time_variance: 5,
            };
        },

        closeModal() {
            this.resetForm();
            this.isModalActive = false;
            $('#' + this.modalId).modal('hide');
        }
    },
    mounted() {
        this.$store.dispatch('getEmployees');
        this.$store.dispatch('getLocations');
        
        if (this.selectedUrl) {
            this.modalTitle = this.$t('edit');
            this.preloader = true;
            // Disable simulation mode for edit
            this.inputs.entry_mode = 'single';
        }
    },
}
</script>

<style>

</style>